import { backendClient } from '@/lib/api-client';
import { getMicrosoftFilesSourceLabel } from '@/lib/microsoftFiles';

export interface BatchProgress {
  batchTaskId: string;
  matterGuid: string;
  source: string;
  totalFiles: number;
  uploadedFiles: number;
  completedFiles: number;
  failedFiles: number;
  syncedDeleteFiles: number;
  /** Subset of failedFiles skipped as duplicates — informational, not a warning. */
  duplicateCount: number;
  uploadProgress: number;
  processingDescription: string;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  startTime: number;
  lastUpdate: number;
  isBundle?: boolean;
  bundleTitle?: string;
  errorMessage?: string;
  folderId?: number;
}

interface UploadBatchApiResponse {
  guid: string;
  matter_guid?: string | null;
  type: string;
  source?: string | null;
  total_files?: number | null;
  uploaded_files?: number | null;
  completed_files?: number | null;
  failed_files?: number | null;
  synced_delete_files?: number | null;
  duplicate_files?: number | null;
  status: BatchProgress['status'];
  created_at?: string | null;
  updated_at?: string | null;
  workflow_metadata?: {
    source_provider?: string | null;
    bundle_structure?: {
      structure?: {
        title?: string | null;
      } | null;
    } | null;
    error_message?: string | null;
  } | null;
  folder_id?: number | null;
}

const pendingKnowledgeCleanupCounts: Record<string, number> = {};

export function getPendingKnowledgeCleanupCount(matterGuid?: string): number {
  return matterGuid ? pendingKnowledgeCleanupCounts[matterGuid] || 0 : 0;
}

export function rememberPendingKnowledgeCleanupCount(matterGuid: string, count: number) {
  pendingKnowledgeCleanupCounts[matterGuid] = count;
}

/**
 * A failed batch where some files never reached completed or failed, such as a
 * drop the monitor abandoned with its files left uploaded. It did not succeed,
 * even though no file failed.
 */
export function batchFailedWithUnfinishedFiles(batch: BatchProgress): boolean {
  return batch.status === 'failed' && batch.completedFiles + batch.failedFiles < batch.totalFiles;
}

function mapBatchToProgress(batch: UploadBatchApiResponse): BatchProgress {
  const createdTime = batch.created_at ? new Date(batch.created_at).getTime() : Date.now();
  const isBundle = batch.type === 'bundle';
  const sourceProvider = String(batch.workflow_metadata?.source_provider || '').toLowerCase();
  const source =
    batch.source === 'onedrive' && sourceProvider === 'sharepoint'
      ? 'sharepoint'
      : (batch.source || 'crimson');
  const totalFiles = batch.total_files || 0;
  const uploadedFiles = batch.uploaded_files || 0;
  const completedFiles = batch.completed_files || 0;
  const failedFiles = batch.failed_files || 0;
  const syncedDeleteFiles = batch.synced_delete_files || 0;
  const duplicateCount = batch.duplicate_files || 0;

  let processingDescription = 'Document upload';
  if (isBundle) {
    processingDescription = 'Bundle generation';
  } else if (batch.source === 'outlook') {
    processingDescription = 'Correspondence';
  } else if (batch.source === 'imanage') {
    processingDescription = 'iManage';
  } else if (batch.source === 'onedrive') {
    processingDescription = getMicrosoftFilesSourceLabel(source);
  }

  // If all files are uploaded but batch still says 'uploading', treat it as 'processing'
  // on the frontend (backend monitor will catch up)
  let effectiveStatus = batch.status;
  if (batch.status === 'uploading' && uploadedFiles === totalFiles && totalFiles > 0) {
    effectiveStatus = 'processing';
  }

  let uploadProgress = 0;
  if (totalFiles > 0) {
    // First 50%: (files uploaded OR failed) / total
    // Second 50%: (files processed successfully OR failed) / total
    const totalFinalized = completedFiles + failedFiles;
    const uploadPhaseComplete = Math.max(uploadedFiles, totalFinalized);
    const uploadPercentage = (uploadPhaseComplete / totalFiles) * 50;
    const processingPercentage = (totalFinalized / totalFiles) * 50;
    uploadProgress = Math.round(uploadPercentage + processingPercentage);
  }

  const bundleTitle = isBundle
    ? batch.workflow_metadata?.bundle_structure?.structure?.title ?? undefined
    : undefined;

  return {
    batchTaskId: batch.guid,
    matterGuid: batch.matter_guid || '',
    source,
    totalFiles,
    uploadedFiles,
    completedFiles,
    failedFiles,
    syncedDeleteFiles,
    duplicateCount,
    uploadProgress,
    processingDescription,
    status: effectiveStatus,
    startTime: createdTime,
    lastUpdate: batch.updated_at ? new Date(batch.updated_at).getTime() : createdTime,
    isBundle,
    bundleTitle,
    errorMessage: batch.workflow_metadata?.error_message ?? undefined,
    folderId: batch.folder_id ?? undefined,
  };
}

/**
 * Poll active + recent document import batches from backend
 * Returns current state for BackgroundSyncContext
 */
export async function pollFileUploadProgress(matterGuid?: string): Promise<BatchProgress[]> {
  try {
    if (!matterGuid) return [];
    const response = await backendClient.get(`/correspondence/${matterGuid}/upload-batches`, {
      params: { limit: 10 },
    });
    rememberPendingKnowledgeCleanupCount(matterGuid, response.data.pending_knowledge_cleanup_count || 0);
    return (response.data.batches || []).map(mapBatchToProgress);
  } catch (error) {
    console.error('Failed to poll upload progress:', error);
    return [];
  }
}

/**
 * Fetch a single page of upload batches. Used for paginating older batches
 * beneath the live-polled window in the upload manager.
 */
export async function fetchUploadBatchesPage(
  matterGuid: string,
  limit: number,
  offset: number
): Promise<BatchProgress[]> {
  const response = await backendClient.get(`/correspondence/${matterGuid}/upload-batches`, {
    params: { limit, offset },
  });
  return (response.data.batches || []).map(mapBatchToProgress);
}
